import { AppRoutes } from '@/shared/constants/app-routes.constant';

const KEYCLOAK_REALM = 'hdata';
const KEYCLOAK_CLIENT_ID = 'hdata-frontend';
const KEYCLOAK_AUTH_STATE_STORAGE_KEY = 'keycloak auth state';
const KEYCLOAK_CODE_VERIFIER_STORAGE_KEY = 'keycloak code verifier';
const KEYCLOAK_ID_TOKEN_STORAGE_KEY = 'keycloak id token';

type ManagedLoginTokens = {
	access_token: string;
	refresh_token: string;
	id_token?: string;
};

const getKeycloakOrigin = (): string => new URL(USER_API_URL, window.location.origin).origin;

const getKeycloakOpenIdBaseUrl = (): URL => {
	return new URL(`/keycloak/realms/${KEYCLOAK_REALM}/protocol/openid-connect/`, getKeycloakOrigin());
};

export const getManagedLoginRedirectUri = (): string =>
	new URL(AppRoutes.LoginCallback, window.location.origin).toString();
const getManagedLogoutRedirectUri = (): string => new URL(AppRoutes.Login, window.location.origin).toString();

const encodeBase64Url = (bytes: Uint8Array): string => {
	const binary = Array.from(bytes, (value) => String.fromCharCode(value)).join('');

	return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
};

const createRandomString = (size: number): string => {
	const randomBytes = new Uint8Array(72);
	window.crypto.getRandomValues(randomBytes);

	return encodeBase64Url(randomBytes).slice(0, size);
};

const createCodeChallenge = async (codeVerifier: string): Promise<string> => {
	const encodedVerifier = new TextEncoder().encode(codeVerifier);
	const digest = await window.crypto.subtle.digest('SHA-256', encodedVerifier);

	return encodeBase64Url(new Uint8Array(digest));
};

export const redirectToManagedLogin = async (): Promise<void> => {
	const authUrl = new URL('auth', getKeycloakOpenIdBaseUrl());
	const state = createRandomString(32);
	const codeVerifier = createRandomString(96);
	const codeChallenge = await createCodeChallenge(codeVerifier);

	sessionStorage.setItem(KEYCLOAK_AUTH_STATE_STORAGE_KEY, state);
	sessionStorage.setItem(KEYCLOAK_CODE_VERIFIER_STORAGE_KEY, codeVerifier);

	authUrl.searchParams.set('client_id', KEYCLOAK_CLIENT_ID);
	authUrl.searchParams.set('redirect_uri', getManagedLoginRedirectUri());
	authUrl.searchParams.set('response_type', 'code');
	authUrl.searchParams.set('scope', 'openid');
	authUrl.searchParams.set('state', state);
	authUrl.searchParams.set('code_challenge', codeChallenge);
	authUrl.searchParams.set('code_challenge_method', 'S256');

	window.location.assign(authUrl.toString());
};

export const exchangeManagedLoginCode = async (): Promise<ManagedLoginTokens> => {
	const currentUrl = new URL(window.location.href);
	const code = currentUrl.searchParams.get('code');
	const state = currentUrl.searchParams.get('state');
	const expectedState = sessionStorage.getItem(KEYCLOAK_AUTH_STATE_STORAGE_KEY);
	const codeVerifier = sessionStorage.getItem(KEYCLOAK_CODE_VERIFIER_STORAGE_KEY);

	if (!code || !state || !expectedState || !codeVerifier || state !== expectedState) {
		sessionStorage.removeItem(KEYCLOAK_AUTH_STATE_STORAGE_KEY);
		sessionStorage.removeItem(KEYCLOAK_CODE_VERIFIER_STORAGE_KEY);
		throw new Error('Invalid Keycloak login state');
	}

	sessionStorage.removeItem(KEYCLOAK_AUTH_STATE_STORAGE_KEY);
	sessionStorage.removeItem(KEYCLOAK_CODE_VERIFIER_STORAGE_KEY);

	const requestBody = new URLSearchParams({
		grant_type: 'authorization_code',
		client_id: KEYCLOAK_CLIENT_ID,
		code,
		redirect_uri: getManagedLoginRedirectUri(),
		code_verifier: codeVerifier,
	});

	const response = await fetch(new URL('token', getKeycloakOpenIdBaseUrl()).toString(), {
		method: 'POST',
		headers: {
			'Content-Type': 'application/x-www-form-urlencoded',
		},
		body: requestBody.toString(),
	});

	if (!response.ok) {
		throw new Error(`Authorization code exchange failed with status ${response.status}`);
	}

	const tokens = (await response.json()) as ManagedLoginTokens;

	if (tokens.id_token) {
		sessionStorage.setItem(KEYCLOAK_ID_TOKEN_STORAGE_KEY, tokens.id_token);
	}

	return tokens;
};

export const redirectToManagedLogout = (): void => {
	const logoutUrl = new URL('logout', getKeycloakOpenIdBaseUrl());
	const idTokenHint = sessionStorage.getItem(KEYCLOAK_ID_TOKEN_STORAGE_KEY);

	sessionStorage.removeItem(KEYCLOAK_AUTH_STATE_STORAGE_KEY);
	sessionStorage.removeItem(KEYCLOAK_CODE_VERIFIER_STORAGE_KEY);
	sessionStorage.removeItem(KEYCLOAK_ID_TOKEN_STORAGE_KEY);

	logoutUrl.searchParams.set('client_id', KEYCLOAK_CLIENT_ID);
	logoutUrl.searchParams.set('post_logout_redirect_uri', getManagedLogoutRedirectUri());

	if (idTokenHint) {
		logoutUrl.searchParams.set('id_token_hint', idTokenHint);
	}

	window.location.assign(logoutUrl.toString());
};
